import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { AppContext, OrderDeletionContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import {
  getOpenQuotesSelectList,
  getQuoteDetails,
} from '../../clients/quote_client';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { createOrder, updateOrder } from '../../clients/order_client';
import {
  getProjectDetails,
  getProjectSelectList,
} from '../../clients/project_client';
import { getUsersForOrder } from '../../clients/user_client';
import * as yup from 'yup';
import { Col, Row, Form, Spinner, Badge } from 'react-bootstrap';
import { Formik } from 'formik';
import { FiletypePdf, PencilFill } from 'react-bootstrap-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  showToast,
  updateObjectInObjectArray,
} from '../../config_and_helpers/helpers';
import { getSupplierCredits } from '../../clients/supplier_credit_client';
import {
  buildOrderQuoteFundingPatchFields,
  getOrderEditBudgetIdInitialValue,
  getOrderEditFundingCreditIdInitialValue,
  getOrderEditFundingSourceTypeInitialValue,
  isOrderQuoteFundingEditable,
  resolveOrderAmountCharged,
} from '../../utils/quoteFundingDisplay';
import BudgetLineSelect from '../LabData/BudgetLineSelect';
import {
  applySubmittedQuoteFundingToOrder,
  mergeOrderAfterUpdate,
} from '../../utils/mergeOrderAfterUpdate';
import OrderCache from '../../config_and_helpers/order_cache';
import { logInfo, logError } from '../../config_and_helpers/logger';
import {
  findUserOption,
  withOptionalNoUser,
} from '../../config_and_helpers/userDropdownOptions';
import OrderItemComponent from './OrderItemComponent';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import S3MediaLink from '../Generic/S3MediaLink';
import DropdownSelect from '../Generic/DropdownSelect';
import CustomOrderContextHook from '../../pages/Order/CustomOrderContextHook';
import CustomProductContextHook from '../../pages/Product/CustomProductContextHook';
import {
  collectKitIdsFromOrderItems,
  collectKitIdsFromProductList,
  refreshParentKitsInProductList,
} from '../../utils/kitStockLiveRefresh';
import PdfUploadComponent from './PdfUploadComponent';
import { useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import '../Glass/GlassStyle.css';
import './OrderComponentStyle.css';
import { parseNumericInput, yupNumericString } from '../../utils/numberInput';
import { hasAnyItemQuantityMismatch } from '../../utils/orderQuantityValidation';
import {
  buildOrderFilesManifestJson,
  buildOrderFilesToDelete,
  countOrderFiles,
  orderFileStateFromOrder,
  pendingOrderFileRows,
} from '../../utils/orderFiles';
import OrderFilesUploadPanels from './OrderFilesUploadPanels';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import { useLabStorageNodes } from '../Storage/useLabStorageNodes';
import { buildStoragePlaceOptions, parseStoragePlace } from '../../utils/storagePlace';
import { mapReceptionLotToFormLot } from '../../utils/orderReceptionFormItems';

/**
 * ItemSchema Yup Validation Schema
 *
 * The validation schema for each individual item in the order.
 * Validates the quantity, expiry date, item fulfillment status, selected reason,
 * and other reason details of an item when item's fulfillment status marked as false.
 *
 * @type {yup.object}
 */
const itemSchema = yup.object().shape({
  orderItemQuantity: yupNumericString({
    required: true,
    integerOnly: true,
    min: 0,
    message: 'Quantity must be a positive number',
  }),
  itemFulfilled: yup.boolean(),
  storageLocation: yup.string(),
  selectedReason: yup.string().when('itemFulfilled', function (itemFulfilled) {
    if (itemFulfilled === false) {
      return yup.string().required('Reason is required');
    }
    return yup.string();
  }),
  otherReasonDetail: yup
    .string()
    .when('selectedReason', function (selectedReason) {
      if (selectedReason === 'Other') {
        return yup.string().required('Detail is required when status is Other');
      }
      return yup.string();
    }),
});

/**
 * createFormSchema Yup Validation Schema
 *
 * A factory function that returns a Yup object schema appropriate for validating the form used in the component.
 * It validates the items list, quote list, reception dates, order images and received by fields.
 *
 * Order documents (PO or receipt) validated in submit via countOrderFiles.
 * @returns {yup.object} - A Yup validation schema
 */
const createFormSchema = ({ isPrepaymentOrder = false }) =>
  yup.object().shape({
    items: isPrepaymentOrder ? yup.mixed() : yup.array().of(itemSchema),
    quoteList: yup.string(),
    receptionDates: yup
      .array()
      .of(
        yup
          .date()
          .required('Reception date is required.')
          .typeError('Invalid date format. Please enter a valid date.')
          .max(new Date(), 'Reception date cannot be in the future')
      )
      .min(1, 'At least one reception date is required.')
      .required('Reception dates are required.'),
    receivedBy: isPrepaymentOrder
      ? yup.string()
      : yup.string().required('Received by field is required.'),
    corporateOrderRef: yup
      .string()
      .required('Order reference field is required.'),
  });

/**
 * Represents a modal component for creating or updating an order.
 * @constructor
 * @param {function} onSuccessfulSubmit - The function to be called when the form submission is successful.
 * @param {object} orderObj - The object representing the order. If provided, the modal will be used to update an existing order.
 * @param {boolean} homeShowModal - A flag indicating whether the modal should be shown when the component is rendered.
 * @param {function} setHomeShowModal - The function to be called to update the flag for showing the modal.
 * @param {function} clearSearchValue - The function to be called to clear the search value.
 */
const OrderModal = ({
  onSuccessfulSubmit,
  orderObj,
  homeShowModal,
  setHomeShowModal,
  clearSearchValue,
  onEditButtonHover,
  isEditButtonLoading,
  projectId, // Optional project ID to associate order with project
  onProjectUpdate, // Optional callback to refresh project data after order creation/update
  onOrderUpdated, // Optional callback to update cached order data after successful edit
}) => {
  // useContext - Fetches the user token from the overall app context.
  const { token, userDetails } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const navigate = useNavigate();
  // Context to manage the re-rendering of the related product modals related to this order
  const { setOrderUpdated, setOrders } = CustomOrderContextHook();
  const { products, setProducts } = CustomProductContextHook();

  const applyMergedOrderToList = mergedOrder => {
    if (!orderObj || typeof setOrders !== 'function') {
      return;
    }
    setOrders(prev =>
      Array.isArray(prev)
        ? updateObjectInObjectArray(prev, orderObj.id, mergedOrder)
        : prev
    );
  };

  /**
   * After reception, re-read kit CAT#s that were on the order (and already in
   * the product list) so complete-kit counts/lots match backend sync.
   *
   * @param {Object|null} [updatedOrder]
   */
  const refreshKitsAfterReception = updatedOrder => {
    const orderItems = [
      ...(Array.isArray(updatedOrder?.items) ? updatedOrder.items : []),
      ...(Array.isArray(orderObj?.items) ? orderObj.items : []),
      ...(Array.isArray(relatedQuoteObj?.items) ? relatedQuoteObj.items : []),
    ];
    let kitIds = collectKitIdsFromOrderItems(orderItems, products);
    if (!kitIds.length) {
      kitIds = collectKitIdsFromProductList(products);
    }
    if (!token || !kitIds.length) return;
    logInfo('OrderModal', 'kit refresh after reception start', {
      kitIds,
      orderId: updatedOrder?.id ?? orderObj?.id ?? null,
    });
    refreshParentKitsInProductList({
      token,
      products,
      kitIds,
      componentId: 'order-reception',
    })
      .then(({ products: withKits, failedIds }) => {
        if (typeof setProducts === 'function' && withKits.length) {
          setProducts(withKits);
        }
        if (failedIds.length) {
          logError('OrderModal', 'kit refresh after reception failed', {
            failedIds,
          });
        }
      })
      .catch(err => {
        logError('OrderModal', 'kit refresh after reception error', {
          message: err?.message ?? null,
        });
      });
  };

  // useContext - Fetches the isOrderDeleted boolean in order to reset the openQuotesSelectList.
  const { isOrderDeleted, toggleOrderDeleted } =
    useContext(OrderDeletionContext);

  // Create a ref to store previous quantity string
  const prevQuantityStringRef = useRef('');
  // Create a ref to store the items when toggling between allDidNotArrive and allReceivedOk
  const initialItemsRef = useRef([]);
  // Ref to fetch project list only once per modal open (avoids loader/dropdown flicker when list is empty)
  const projectsFetchAttemptedRef = useRef(false);

  const [relatedQuoteObj, setRelatedQuoteObj] = useState(null);
  const [orderDocumentsError, setOrderDocumentsError] = useState('');

  const isPrepaymentOrder =
    (relatedQuoteObj?.prepayment_amount != null &&
      relatedQuoteObj?.prepayment_amount !== '') ||
    (orderObj?.quote?.prepayment_amount != null &&
      orderObj?.quote?.prepayment_amount !== '');

  const formSchema = createFormSchema({
    isPrepaymentOrder,
  });

  const initialOrderFiles = orderFileStateFromOrder(orderObj);
  const [openQuotesSelectList, setOpenQuotesSelectList] = useState([]);
  const [items, setItems] = useState([]);
  const [poFiles, setPoFiles] = useState(initialOrderFiles.poFiles);
  const [receiptFiles, setReceiptFiles] = useState(
    initialOrderFiles.receiptFiles
  );
  const [notifySupplier, setNotifySupplier] = useState(false);
  const [quantityError, setQuantityError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allDidNotArrive, setAllDidNotArrive] = useState(false);
  const [allReceivedOK, setAllReceivedOK] = useState(false);
  const [formKeyCounter, setFormKeyCounter] = useState(0);
  const [receivedByValue, setReceivedByValue] = useState(
    orderObj ? orderObj.received_by : ''
  );

  /** Default display name for OCR submit_reviewed_ocr `received_by`. */
  const pdfOcrDefaultReceivedBy = useMemo(() => {
    if (!userDetails || typeof userDetails !== 'object') return '';
    const full = `${userDetails.first_name || ''} ${userDetails.last_name || ''}`.trim();
    if (full) return full;
    if (userDetails.username) return String(userDetails.username);
    if (userDetails.email) return String(userDetails.email);
    return '';
  }, [userDetails]);

  const [corporateOrderRefValue, setCorporateOrderRefValue] = useState(
    orderObj ? orderObj.corporate_order_ref : ''
  );
  /** When true, order is omitted from the daily finance reception digest. */
  const [excludeFromFinanceDigestValue, setExcludeFromFinanceDigestValue] =
    useState(Boolean(orderObj?.exclude_from_finance_digest));
  const [receptionDatesValue, setReceptionDatesValue] = useState(
    orderObj && orderObj.reception_dates
      ? orderObj.reception_dates
      : [new Date().toISOString().split('T')[0]]
  );
  const [showModal, setShowModal] = useState(
    homeShowModal ? homeShowModal : false
  );
  const { nodes: storageNodes, ready: storageReady } = useLabStorageNodes(
    token,
    Boolean(showModal)
  );
  const storageTree = useMemo(
    () => (storageReady ? storageNodes : []),
    [storageReady, storageNodes]
  );
  const storagePlaceOptions = useMemo(
    () => (storageReady ? buildStoragePlaceOptions(storageTree) : null),
    [storageReady, storageTree]
  );
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [projectInfo, setProjectInfo] = useState(null);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  // Optional order association: user and project (see docs/ORDER_USER_PROJECT_API.md)
  const [userSelectList, setUserSelectList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [projectSelectList, setProjectSelectList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [supplierCredits, setSupplierCredits] = useState([]);
  const [isLoadingSupplierCredits, setIsLoadingSupplierCredits] =
    useState(false);
  /** Quote funding editor — kept outside Formik so enableReinitialize cannot wipe user edits. */
  const [orderFundingSourceType, setOrderFundingSourceType] =
    useState('budget');
  const [orderFundingBudgetId, setOrderFundingBudgetId] = useState('');
  const [orderFundingCreditId, setOrderFundingCreditId] = useState('');
  const fundingInitializedForOrderRef = useRef(null);

  const linkedQuoteId = useMemo(() => {
    if (!orderObj?.quote) {
      return null;
    }
    return typeof orderObj.quote === 'object'
      ? orderObj.quote.id
      : orderObj.quote;
  }, [orderObj?.quote]);

  const isLinkedQuoteReady =
    !orderObj ||
    linkedQuoteId == null ||
    (relatedQuoteObj != null &&
      String(relatedQuoteObj.id) === String(linkedQuoteId));

  const isLoadingLinkedQuote = Boolean(
    orderObj && linkedQuoteId != null && !isLinkedQuoteReady
  );

  const orderSupplierId = useMemo(() => {
    const supplier = orderObj?.supplier ?? relatedQuoteObj?.supplier;
    if (!supplier) {
      return null;
    }
    return typeof supplier === 'object' ? supplier.id : supplier;
  }, [orderObj?.supplier, relatedQuoteObj?.supplier]);

  const userDropdownOptions = useMemo(
    () => withOptionalNoUser(userSelectList),
    [userSelectList]
  );

  const triggerFormReset = reason => {
    if (process.env.NODE_ENV === 'development') {
      logInfo('OrderModal', 'Triggering explicit Formik remount', {
        reason,
        relatedQuoteId: relatedQuoteObj?.id || null,
        orderId: orderObj?.id || null,
      });
    }
    setFormKeyCounter(prev => prev + 1);
  };

  // This function toggles all item statuses
  // ========= helpers =========
  const deepCopyItems = src =>
    src.map(item => ({
      ...item,
      lotItems: item.lotItems.map(lot => ({ ...lot })),
      kitComponentLots: item.kitComponentLots
        ? item.kitComponentLots.map(kitComponentLot => ({
            ...kitComponentLot,
            lots: kitComponentLot.lots.map(lot => ({ ...lot })),
          }))
        : [],
    }));

  // ========= DID NOT ARRIVE =========
  const handleAllDidNotArriveToggle = checked => {
    setAllDidNotArrive(checked);
    setAllReceivedOK(false); // keep toggles mutually exclusive

    if (checked) {
      /* ────────────────────────────────────────────────────────────────
       1)  “Did not arrive” → clear quantities & lot items
    ──────────────────────────────────────────────────────────────── */
      if (orderObj) {
        // store a snapshot so the user can come back to exactly this state
        initialItemsRef.current = items.map(it => ({
          ...it,
          lotItems: it.lotItems.map(lot => ({ ...lot })),
        }));
      }

      setItems(prev =>
        prev.map(it => ({
          ...it,
          status: 'Did not arrive',
          orderItemQuantity: 0,
          lotItems: [],
          kitComponentLots: [],
          issueDetail: '',
        }))
      );
      setReceivedByValue('TBD');

      // Explicit hard reset because toggle changes structure of items/lots.
      triggerFormReset('all-did-not-arrive-enabled');
    } else {
      /* ────────────────────────────────────────────────────────────────
       2)  Toggle OFF  → restore initial values
           • Edit-mode : rebuild from orderObj
           • Create-mode: rebuild from relatedQuoteObj (original logic)
    ──────────────────────────────────────────────────────────────── */
      let restoredItems;

      if (orderObj && orderObj.items) {
        restoredItems = orderObj.items.map(it => {
          // Calculate the actual received quantity from lot items
          const receivedQuantity =
            it.lot_items && it.lot_items.length > 0
              ? it.lot_items.reduce(
                  (sum, lot) => sum + (lot.lot_item_quantity || 0),
                  0
                )
              : 0;

          return {
            quoteItem: {
              id: it.quote_item.id,
              quantity: it.quote_item.quantity,
            },
            catNum: it.product.cat_num,
            orderItemQuantity: receivedQuantity,
            storageLocation:
              it?.storage_location || it?.product?.location || '',
            lotItems:
              it.kit_component_lots && it.kit_component_lots.length > 0
                ? [{ expiry: '', batch: '', lotItemQuantity: 0 }] // Kit products get one default lot item for main tracking
                : it.lot_items.map(lot => ({
                    batch: lot.batch,
                    expiry: lot.expiry,
                    lotItemQuantity: lot.lot_item_quantity,
                  })),
            kitComponentLots:
              it.kit_component_lots && it.kit_component_lots.length > 0
                ? it.lot_items.map((lotItem, index) => ({
                    componentId:
                      it.kit_component_lots[index]?.component_id ||
                      `component_${index}`,
                    lots: [
                      {
                        batch: lotItem.batch || '',
                        expiry: lotItem.expiry || '',
                        storageLocation: lotItem.storage_location || '',
                      },
                    ],
                  }))
                : [],
            status: it.status,
            issueDetail: it.issue_detail,
          };
        });
      } else if (relatedQuoteObj) {
        // recreate using the same logic as initial mount
        const mapped = relatedQuoteObj.items.map(it => ({
          quoteItem: { id: it.id, quantity: it.quantity },
          catNum: it.product.cat_num,
          orderItemQuantity: it.quantity,
          status: 'OK',
          storageLocation: it?.product?.location || '',
          lotItems: it.lotItems?.length
            ? it.lotItems.map(lot => ({
                ...lot,
                lotItemQuantity: '', // reset quantity only
              }))
            : [{ expiry: '', batch: '', lotItemQuantity: '' }],
          kitComponentLots: [],
          issueDetail: '',
        }));
        restoredItems = calculateOrderLotQuantities(mapped);
      } else {
        restoredItems = []; // (safety fallback)
      }

      setItems(restoredItems);
      if (receivedByValue === 'TBD') setReceivedByValue('');

      triggerFormReset('all-did-not-arrive-disabled');
    }
  };

  // ========= RECEIVED OK =========
  const handleAllReceivedOKToggle = (checked, applyToForm) => {
    setAllReceivedOK(checked);
    setAllDidNotArrive(false); // keep toggles mutually exclusive

    if (checked) {
      if (process.env.NODE_ENV === 'development') {
        logInfo('OrderModal', 'Mark all as Received OK toggled ON', {
          itemsCount: items.length,
          currentItemsSummary: items.map(it => ({
            orderItemQuantity: it.orderItemQuantity,
            status: it.status,
            lotItemsLength: (it.lotItems || []).length,
          })),
        });
      }

      /* ─── Snapshot current state before mutating ─────────────────── */
      initialItemsRef.current = deepCopyItems(items);

      /* ─── Apply “All OK” logic ───────────────────────────────────── */
      const newItems = items.map(it => {
        // Use the original ordered/quote quantity so "Mark all OK" sets totals and batches to full order
        const receivedQuantity = it.quoteItem?.quantity ?? 0;

        // For kit products, provide default lot items for main tracking
        const updatedLots =
          it.kitComponentLots && it.kitComponentLots.length > 0
            ? [{ batch: '', expiry: '', lotItemQuantity: receivedQuantity }] // Kit products get one default lot item
            : it.lotItems && it.lotItems.length > 0
              ? it.lotItems.map(l => ({
                  ...l,
                  lotItemQuantity: receivedQuantity,
                }))
              : [{ batch: '', expiry: '', lotItemQuantity: receivedQuantity }];

        return {
          ...it,
          status: 'OK',
          orderItemQuantity: receivedQuantity,
          lotItems: updatedLots,
          kitComponentLots: it.kitComponentLots || [],
          issueDetail: '',
        };
      });

      if (typeof applyToForm === 'function') {
        // Commit allReceivedOK and items so the UI (which reads from item when allReceivedOK)
        // updates immediately; then sync Formik for submit.
        flushSync(() => {
          setAllReceivedOK(true);
          setItems(newItems);
        });
        if (process.env.NODE_ENV === 'development') {
          logInfo(
            'OrderModal',
            'Mark all OK: after flushSync (committed state)',
            {
              newItemsFirst: newItems[0]
                ? {
                    orderItemQuantity: newItems[0].orderItemQuantity,
                    lotItemsLength: (newItems[0].lotItems || []).length,
                  }
                : null,
            }
          );
        }
        const formItems = itemsToFormItems(newItems);
        if (process.env.NODE_ENV === 'development') {
          logInfo('OrderModal', 'Mark all OK: syncing Formik with new items', {
            newItemsSummary: newItems.map(it => ({
              orderItemQuantity: it.orderItemQuantity,
              lotItemsLength: (it.lotItems || []).length,
            })),
            formItemsCount: formItems.length,
            formItems,
          });
        }
        applyToForm(newItems);
      } else {
        setItems(newItems);
        triggerFormReset('all-received-ok-enabled-fallback');
      }
    } else {
      /* ─── Untick “All OK” ────────────────────────────────────────── */
      if (orderObj && orderObj.items) {
        /* ▸ EDIT-MODE: fully restore original order items */
        const restoredFromOrder = orderObj.items.map(srv => ({
          quoteItem: {
            id: srv.quote_item.id,
            quantity: srv.quote_item.quantity,
          },
          catNum: srv.product.cat_num,
          orderItemQuantity: srv.quantity,
          storageLocation: srv?.product?.location || '',
          lotItems: (srv.lot_items || []).map(lot => ({
            batch: lot.batch,
            expiry: lot.expiry,
            lotItemQuantity: lot.lot_item_quantity,
          })),
          kitComponentLots: (srv.kit_component_lots || []).map(
            kitComponentLot => ({
              componentId: kitComponentLot.component_id,
              lots: (kitComponentLot.lots || []).map(lot => ({
                batch: lot.batch || '',
                expiry: lot.expiry || '',
                storageLocation: lot.storage_location || '',
              })),
            })
          ),
          status: srv.status,
          issueDetail: srv.issue_detail,
        }));
        setItems(restoredFromOrder);

        triggerFormReset('all-received-ok-disabled-edit-mode');
      } else {
        /* ▸ CREATE-MODE: only clear lot quantities */
        setItems(prev =>
          prev.map(it => ({
            ...it,
            lotItems: (it.lotItems || []).map(l => ({
              ...l,
              lotItemQuantity: '',
            })),
          }))
        );

        triggerFormReset('all-received-ok-disabled-create-mode');
      }
    }
  };

  /**
   * Maps modal items state to the form's items shape (for Formik initialValues and setValues).
   * Each lot keeps its storage place so a later edit of batch, expiry, or quantity
   * does not clear the node (`mapReceptionLotToFormLot`).
   */
  const itemsToFormItems = itemsArray => {
    if (!itemsArray || itemsArray.length === 0) return [];
    return itemsArray.map(item => ({
      orderItemQuantity:
        item.orderItemQuantity != null ? String(item.orderItemQuantity) : '',
      storageLocation: item.storageLocation || '',
      lotItems:
        item.lotItems && item.lotItems.length > 0
          ? item.lotItems.map(lot => mapReceptionLotToFormLot(lot))
          : [],
      kitComponentLots: item.kitComponentLots || [],
      itemFulfilled: item.status === 'OK',
      selectedReason: item.status || '',
      otherReasonDetail: item.issueDetail || '',
    }));
  };

  // Helper to calculate the lot quantities against the original ordered quantity
  const calculateOrderLotQuantities = orderItems => {
    return orderItems.map(item => {
      // Convert orderItemQuantity to number, default to 0 if invalid
      const orderQuantity =
        parseNumericInput(item.orderItemQuantity, { integerOnly: true }) || 0;

      // Sum all lot item quantities for this order item, handling strings
      const totalLotQuantity =
        item.lotItems && item.lotItems.length > 0
          ? item.lotItems.reduce((sum, lot) => {
              // Parse string to float, default to 0 if empty/invalid
              const lotQuantity =
                parseNumericInput(lot.lotItemQuantity, { integerOnly: true }) || 0;
              return sum + lotQuantity;
            }, 0)
          : 0;

      const quantityDifference = totalLotQuantity - orderQuantity;

      return {
        ...item,
        lotQuantityMatch: {
          isMatching: Math.abs(totalLotQuantity - orderQuantity) < 0.001, // Using small epsilon for float comparison
          difference: quantityDifference,
          totalLotQuantity,
        },
      };
    });
  };

  // UseEffect to update the items state when the order object is loaded
  useEffect(() => {
    // Initialize items when orderObj changes
    if (orderObj && orderObj.items) {
      const mappedItems = orderObj.items.map(item => {
        // Handle lot items based on item status
        let lotItems = [];
        let kitComponentLots = [];

        if (item.status === 'Did not arrive') {
          // "Did not arrive" items should have NO lot items
          lotItems = [];
          kitComponentLots = [];
        } else if (item.lot_items && item.lot_items.length > 0) {
          // Check if this is a kit product by looking for kit_component_lots
          if (item.kit_component_lots && item.kit_component_lots.length > 0) {
            // This is a kit product - map both regular lot items AND kit component lots
            // Map regular lot items for main order tracking
            lotItems = item.lot_items.map(lotItem => ({
              expiry: lotItem.expiry || '',
              batch: lotItem.batch || '',
              lotItemQuantity: lotItem.lotItemQuantity || 0,
              stockItemIds: lotItem.stockItemIds || [],
            }));

            // Map kit component lots directly from kit_component_lots data
            kitComponentLots = item.kit_component_lots.map(component => ({
              componentId: component.component_id,
              lots: component.lots.map(lot => ({
                batch: lot.batch || '',
                expiry: lot.expiry || '',
                storageLocation: lot.storage_location || '',
              })),
            }));
          } else {
            // This is a regular product - map lot items normally
            lotItems = item.lot_items.map(lotItem => ({
              expiry: lotItem.expiry || '',
              batch: lotItem.batch || '',
              lotItemQuantity: lotItem.lotItemQuantity || 0,
              stockItemIds: lotItem.stockItemIds || [],
            }));
          }
        } else {
          // Items without lot data but not "Did not arrive" - provide default
          if (item.kit_component_lots && item.kit_component_lots.length > 0) {
            // Kit product without lot data - provide default lot item for main tracking
            lotItems = [{ expiry: '', batch: '', lotItemQuantity: 0 }];
            kitComponentLots = [];
          } else {
            // Regular product without lot data
            lotItems = [
              {
                expiry: '',
                batch: '',
                lotItemQuantity: 0,
                stockItemIds: [],
              },
            ];
          }
        }

        // Calculate the actual received quantity from lot items
        const receivedQuantity =
          item.lot_items && item.lot_items.length > 0
            ? item.lot_items.reduce(
                (sum, lot) => sum + (lot.lotItemQuantity || 0),
                0
              )
            : 0;

        return {
          quoteItem: {
            id: item.quote_item.id,
            quantity: item.quote_item.quantity,
          },
          catNum: item.product.cat_num,
          orderItemQuantity: receivedQuantity,
          storageLocation:
            item?.storage_location || item?.product?.location || '',
          lotItems: lotItems,
          kitComponentLots: kitComponentLots,
          status: item.status,
          issueDetail: item.issue_detail,
        };
      });

      // Set the items state
      setItems(mappedItems);
    }

    // Update form field values when orderObj changes (this is safe to do frequently)
    if (orderObj) {
      // Handle received_by: convert to string if it's a number (user ID), or keep as string (legacy text)
      const receivedBy = orderObj.received_by
        ? String(orderObj.received_by)
        : '';
      setReceivedByValue(receivedBy);
      setCorporateOrderRefValue(orderObj.corporate_order_ref || '');
      setExcludeFromFinanceDigestValue(
        Boolean(orderObj.exclude_from_finance_digest)
      );
      setReceptionDatesValue(
        orderObj.reception_dates || [new Date().toISOString().split('T')[0]]
      );
      const files = orderFileStateFromOrder(orderObj);
      setPoFiles(files.poFiles);
      setReceiptFiles(files.receiptFiles);
    } else {
      // Reset values when no orderObj (creating new order)
      setReceivedByValue('');
      setCorporateOrderRefValue('');
      setExcludeFromFinanceDigestValue(false);
      setReceptionDatesValue([new Date().toISOString().split('T')[0]]);
      setPoFiles([]);
      setReceiptFiles([]);
    }
    setOrderDocumentsError('');
  }, [orderObj]);

  // useEffect to to track quantity changes
  useEffect(() => {
    // Create a string of all the quantities to compare changes
    const currentQuantityString =
      items && items.length > 0
        ? items
            .map(item => {
              const lotQuantities =
                item.lotItems && item.lotItems.length > 0
                  ? item.lotItems.map(lot => lot.lotItemQuantity).join(',')
                  : '';
              return `${item.orderItemQuantity}-${lotQuantities}`;
            })
            .join('|')
        : '';

    // Compare with previous quantity string
    if (currentQuantityString !== prevQuantityStringRef.current) {
      // Update the ref with current value
      prevQuantityStringRef.current = currentQuantityString;

      // Recalculate quantities
      setItems(prevItems => calculateOrderLotQuantities(prevItems));
    }
  }, [items]);

  // Disable submit when any item's batch total does not match received quantity.
  useEffect(() => {
    setQuantityError(hasAnyItemQuantityMismatch(items));
  }, [items]);

  // Function to fetch a specific Quote data.
  const fetchQuote = quoteId => {
    getQuoteDetails(token, quoteId, setRelatedQuoteObj);
  };

  // Fetch linked quote when editing an order (budget + supplier context).
  useEffect(() => {
    if (!showModal || !orderObj?.quote) {
      return;
    }
    const quoteId =
      typeof orderObj.quote === 'object' ? orderObj.quote.id : orderObj.quote;
    if (quoteId) {
      fetchQuote(quoteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, orderObj?.id, orderObj?.quote]);

  useEffect(() => {
    if (!showModal || !orderObj?.id || !isLinkedQuoteReady) {
      return;
    }
    const initKey = `${orderObj.id}:${relatedQuoteObj?.id ?? linkedQuoteId ?? 'none'}`;
    if (fundingInitializedForOrderRef.current === initKey) {
      return;
    }
    fundingInitializedForOrderRef.current = initKey;
    setOrderFundingSourceType(
      getOrderEditFundingSourceTypeInitialValue(orderObj, relatedQuoteObj)
    );
    setOrderFundingBudgetId(
      getOrderEditBudgetIdInitialValue(orderObj, relatedQuoteObj)
    );
    setOrderFundingCreditId(
      getOrderEditFundingCreditIdInitialValue(orderObj, relatedQuoteObj)
    );
    logInfo('OrderModal', 'order funding editor initialized', {
      orderId: orderObj.id,
      quoteId: relatedQuoteObj?.id ?? linkedQuoteId,
      fundingSourceType: getOrderEditFundingSourceTypeInitialValue(
        orderObj,
        relatedQuoteObj
      ),
      budgetId: getOrderEditBudgetIdInitialValue(orderObj, relatedQuoteObj),
    });
  }, [
    showModal,
    orderObj,
    relatedQuoteObj,
    isLinkedQuoteReady,
    linkedQuoteId,
  ]);

  useEffect(() => {
    if (!showModal || !orderObj || !orderSupplierId) {
      setSupplierCredits([]);
      return;
    }
    let isCancelled = false;
    setIsLoadingSupplierCredits(true);
    getSupplierCredits(token, orderSupplierId)
      .then(credits => {
        if (isCancelled) return;
        setSupplierCredits(Array.isArray(credits) ? credits : []);
      })
      .catch(() => {
        if (!isCancelled) {
          setSupplierCredits([]);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingSupplierCredits(false);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [showModal, orderObj, orderSupplierId, token]);

  // Function to fetch open quotes select list on hover
  const fetchOpenQuotesSelectList = useCallback(() => {
    setIsLoadingQuotes(true);
    getOpenQuotesSelectList(token, data => {
      setOpenQuotesSelectList(data);
      setIsLoadingQuotes(false);
    });
  }, [token]);

  // Fetch open quotes select list when modal is opened (fallback)
  useEffect(() => {
    if (showModal) {
      fetchOpenQuotesSelectList();
    }
    if (isOrderDeleted) {
      toggleOrderDeleted();
    }
  }, [
    showModal,
    isOrderDeleted,
    fetchOpenQuotesSelectList,
    toggleOrderDeleted,
  ]);

  // Determine project ID and fetch project info for display
  useEffect(() => {
    const determineProjectId = () => {
      // Priority: projectId prop > dropdown selection > orderObj.project > relatedQuoteObj.project
      if (projectId != null) {
        return typeof projectId === 'object' ? projectId.id : projectId;
      }
      if (selectedProject != null) return selectedProject;
      if (orderObj?.project) {
        return typeof orderObj.project === 'object'
          ? orderObj.project.id
          : orderObj.project;
      }
      if (relatedQuoteObj?.project) {
        return typeof relatedQuoteObj.project === 'object'
          ? relatedQuoteObj.project.id
          : relatedQuoteObj.project;
      }
      return null;
    };

    const projectIdToFetch = determineProjectId();

    if (projectIdToFetch && showModal && !isLoadingProject) {
      setIsLoadingProject(true);
      getProjectDetails(token, projectIdToFetch, () => {})
        .then(response => {
          if (response && response.success) {
            setProjectInfo(response.projectData);
          }
          setIsLoadingProject(false);
        })
        .catch(error => {
          logError('OrderModal', 'Error fetching project info', error);
          setProjectInfo(null);
          setIsLoadingProject(false);
        });
    } else if (!projectIdToFetch) {
      setProjectInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showModal,
    projectId,
    selectedProject,
    orderObj?.project,
    relatedQuoteObj?.project,
    token,
  ]);

  // Fetch users for order when modal opens
  useEffect(() => {
    if (showModal && token) {
      setIsLoadingUsers(true);
      getUsersForOrder(token)
        .then(users => {
          if (Array.isArray(users) && !users.error) {
            const formatted = users.map(user => ({
              value: user.id,
              label:
                `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                `User ${user.id}`,
            }));
            setUserSelectList(formatted);
          } else {
            setUserSelectList([]);
          }
          setIsLoadingUsers(false);
        })
        .catch(() => {
          setUserSelectList([]);
          setIsLoadingUsers(false);
        });
    }
  }, [showModal, token]);

  // Fetch project select list once per modal open
  useEffect(() => {
    if (showModal && !projectsFetchAttemptedRef.current) {
      projectsFetchAttemptedRef.current = true;
      setIsLoadingProjects(true);
      getProjectSelectList(token, setProjectSelectList)
        .then(() => setIsLoadingProjects(false))
        .catch(() => {
          setProjectSelectList([]);
          setIsLoadingProjects(false);
        });
    }
  }, [showModal, token]);

  // Reset selected user and project when modal closes; sync from order/quote when modal opens
  useEffect(() => {
    if (!showModal) {
      projectsFetchAttemptedRef.current = false;
      setSelectedUser(null);
      setSelectedProject(null);
      setProjectSelectList([]);
    } else {
      const userId =
        orderObj?.user != null
          ? typeof orderObj.user === 'object'
            ? orderObj.user.id
            : orderObj.user
          : relatedQuoteObj?.user != null
            ? typeof relatedQuoteObj.user === 'object'
              ? relatedQuoteObj.user.id
              : relatedQuoteObj.user
            : null;
      setSelectedUser(userId);
      const projectVal =
        projectId != null
          ? typeof projectId === 'object'
            ? projectId.id
            : projectId
          : orderObj?.project != null
            ? typeof orderObj.project === 'object'
              ? orderObj.project.id
              : orderObj.project
            : relatedQuoteObj?.project != null
              ? typeof relatedQuoteObj.project === 'object'
                ? relatedQuoteObj.project.id
                : relatedQuoteObj.project
              : null;
      setSelectedProject(projectVal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showModal,
    projectId,
    orderObj?.user,
    orderObj?.project,
    relatedQuoteObj?.user,
    relatedQuoteObj?.project,
  ]);

  // Function to filter out a quote from the open quotes list after an order was created from that quote
  const filterOpenQuoteById = id => {
    const filteredOpenQuotesSelectList = openQuotesSelectList.filter(
      quote => quote.value !== id
    );
    setOpenQuotesSelectList(filteredOpenQuotesSelectList);
  };

  // useEffect Hook to set the Items state based on the relatedQuoteObj state.
  // Runs when relatedQuoteObj changes.
  useEffect(() => {
    if (relatedQuoteObj && !orderObj) {
      const mappedItems = relatedQuoteObj.items.map(item => {
        const existingLotItems = item.lotItems?.length
          ? item.lotItems.map(lot => ({
              ...lot,
              lotItemQuantity: '', // Reset only the quantity
            }))
          : [
              {
                expiry: '',
                batch: '',
                lotItemQuantity: '',
              },
            ];

        // Initialize kit component lots for kit products
        let kitComponentLots = [];
        if (item.product && item.product.is_kit) {
          // For kit products, we'll initialize with empty lots that will be populated later
          // The actual kit components will be loaded by OrderItemComponent when it mounts
          kitComponentLots = []; // Will be populated by OrderItemComponent
        }

        return {
          quoteItem: {
            id: item.id,
            quantity: item.quantity,
          },
          catNum: item.product.cat_num,
          orderItemQuantity: item.quantity,
          status: 'OK',
          storageLocation:
            item?.storage_location || item?.product?.location || '',
          lotItems: existingLotItems,
          kitComponentLots: kitComponentLots,
          issueDetail: '',
        };
      });

      const calculatedItems = calculateOrderLotQuantities(mappedItems);
      initialItemsRef.current = calculatedItems;

      setItems(calculatedItems);
    }
  }, [relatedQuoteObj, orderObj]);

  // Helper function to add a new lot item to a specific order item
  const addLotItem = (e, orderItemIndex) => {
    e.preventDefault(); // Prevent default form submission behavior

    const newItems = items.map((item, i) => {
      if (i !== orderItemIndex) return item; // Keep other items unchanged

      // Add a new empty lot item to the specific order item's lotItems array
      return {
        ...item,
        lotItems: [
          ...(item.lotItems || []), // Ensure lotItems array exists
          {
            expiry: '', // New lot item with empty fields
            batch: '',
            lotItemQuantity: '',
          },
        ],
        kitComponentLots: item.kitComponentLots || [],
      };
    });

    setItems(newItems); // Update state with the modified items
  };

  // Helper function to delete a specific lot item from a specific order item
  const deleteLotItem = (orderItemIndex, lotItemIndex) => {
    const newItems = items.map((item, i) => {
      if (i !== orderItemIndex) return item; // Keep other items unchanged

      // Remove the lot item at the specified index
      return {
        ...item,
        lotItems: item.lotItems.filter((_, j) => j !== lotItemIndex),
        kitComponentLots: item.kitComponentLots || [],
      };
    });

    setItems(newItems); // Update state with the modified items
  };

  // Function updateItem - Updates a specific item in the items state
  const updateItem = (orderItemIndex, field, value, lotItemIndex) => {
    const newItems = items.map((item, i) => {
      if (i !== orderItemIndex) return item;

      // 1. Multi-field update (if value is an object and field is null)
      if (
        field === null &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        return {
          ...item,
          ...value,
        };
      }

      // 2. Full replacement of lotItems
      if (field === 'lotItems') {
        return {
          ...item,
          lotItems: value,
          kitComponentLots: item.kitComponentLots || [],
        };
      }

      // 3. Full replacement of kitComponentLots
      if (field === 'kitComponentLots') {
        return {
          ...item,
          kitComponentLots: value,
        };
      }

      if (lotItemIndex != null && field === 'storagePlace' && value && typeof value === 'object') {
        return {
          ...item,
          lotItems: item.lotItems.map((lot, j) =>
            j === lotItemIndex
              ? {
                  ...lot,
                  storageNodeId: value.storageNodeId ?? null,
                  slotIndex: value.slotIndex ?? null,
                }
              : lot
          ),
          kitComponentLots: item.kitComponentLots || [],
        };
      }

      // 3. Single lotItem field update
      if (lotItemIndex != null) {
        return {
          ...item,
          lotItems: item.lotItems.map((lot, j) =>
            j === lotItemIndex ? { ...lot, [field]: value } : lot
          ),
          kitComponentLots: item.kitComponentLots || [], // Preserve kit component lots
        };
      }

      // 4. Normal field update
      return {
        ...item,
        [field]: value,
        kitComponentLots: item.kitComponentLots || [], // Preserve kit component lots
      };
    });

    setItems(newItems);
  };

  const appendPendingFiles = (setFiles, files) => {
    const newRows = (files || []).map(file => ({
      file,
      id: `temp-${Date.now()}-${Math.random()}`,
    }));
    setFiles(prev => [...prev, ...newRows]);
    setOrderDocumentsError('');
  };

  const handleDeletePoFile = fileId => {
    setPoFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleDeleteReceiptFile = fileId => {
    setReceiptFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const hasRequiredOrderDocuments =
    isPrepaymentOrder || countOrderFiles(poFiles, receiptFiles) >= 1;

  /** Linked quote PDF control — create flow: quote row; edit flow: beside supplier bar. */
  const renderQuotePdfLink = () => {
    if (!relatedQuoteObj) {
      return null;
    }

    let linkContent = null;

    if (relatedQuoteObj.quote_url instanceof Blob) {
      linkContent = (
        <a
          href={URL.createObjectURL(relatedQuoteObj.quote_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="procure-quote-pdf-link"
        >
          <FiletypePdf size={20} className="procure-quote-pdf-link__icon" />
          <p className="procure-quote-pdf-link__label">Quote PDF</p>
        </a>
      );
    } else if (relatedQuoteObj.quote_url) {
      linkContent = (
        <S3MediaLink
          url={relatedQuoteObj.quote_url}
          quoteId={relatedQuoteObj.id}
          title="Open quote PDF"
          className="procure-quote-pdf-link"
        >
          <FiletypePdf size={20} className="procure-quote-pdf-link__icon" />
          <p className="procure-quote-pdf-link__label">Quote PDF</p>
        </S3MediaLink>
      );
    }

    if (!linkContent) {
      return null;
    }

    return (
      <div className="order-modal-quote-link-wrap">{linkContent}</div>
    );
  };

  // handleClose - Function to handle the closing of the modal. It reverts all state to its original form.
  const handleClose = () => {
    if (setHomeShowModal) setHomeShowModal(false);
    setShowModal(false);
    const files = orderFileStateFromOrder(orderObj);
    setPoFiles(files.poFiles);
    setReceiptFiles(files.receiptFiles);
    setRelatedQuoteObj(null);
    fundingInitializedForOrderRef.current = null;
    setOrderFundingSourceType('budget');
    setOrderFundingBudgetId('');
    setOrderFundingCreditId('');
    setAllDidNotArrive(false);
    setAllReceivedOK(false);

    // Only clear form values when creating a new order (not editing)
    if (!orderObj) {
      setCorporateOrderRefValue('');
      setReceivedByValue('');
      setReceptionDatesValue([new Date().toISOString().split('T')[0]]);
      setNotifySupplier(false);
    }

    setItems(
      orderObj && orderObj.items
        ? () => {
            return orderObj.items.map(item => ({
              quoteItem: {
                id: item.quote_item.id,
                quantity: item.quote_item.quantity,
              },
              orderItemQuantity: item.quantity,
              storageLocation:
                item?.storage_location || item?.product?.location || '',
              lotItems: item.lot_items,
              kitComponentLots: (item.kit_component_lots || []).map(
                kitComponentLot => ({
                  componentId: kitComponentLot.component_id,
                  lots: (kitComponentLot.lots || []).map(lot => ({
                    batch: lot.batch || '',
                    expiry: lot.expiry || '',
                    storageLocation: lot.storage_location || '',
                  })),
                })
              ),
              status: item.status,
              issueDetail: item.issue_detail,
            }));
          }
        : []
    );
  };

  // handleShow - Function to open the modal by setting showModal to true.
  const handleShow = () => setShowModal(true);

  // handleSubmit - Finishing up the form submission either creating a new order or updating an existing one.
  const handleSubmit = values => {
    try {
      // Indicates that the submission process has started, typically used to handle UI changes such as showing a loading spinner.
      setIsSubmitting(true);

      if (!isPrepaymentOrder && countOrderFiles(poFiles, receiptFiles) < 1) {
        setOrderDocumentsError(
          'Upload at least one file: a purchase order (PO) or a receipt/invoice.'
        );
        setIsSubmitting(false);
        return;
      }
      setOrderDocumentsError('');

      for (const formItem of values.items || []) {
        for (const lotItem of formItem.lotItems || []) {
          if (lotItem.storageNodeId == null || lotItem.storageNodeId === '') continue;
          const place = parseStoragePlace(
            storageTree,
            lotItem.storageNodeId,
            lotItem.slotIndex
          );
          if (place.error) {
            showToast(place.error, 'warning', 'top-right', 3500);
            setIsSubmitting(false);
            return;
          }
        }
      }

      let finalItems;

      // Map over the Formik values array to get the actual form data including kit component lots
      // This ensures we get the user-entered data from the form fields
      finalItems = values.items.map((formItem, index) => {
        // Get the corresponding item from items state for additional data
        const item = items[index];
        const cleanLotItems = (formItem.lotItems || []).map(lotItem => {
          // First clean the empty fields
          let cleanLotItem = {
            lot_item_quantity: lotItem.lotItemQuantity,
          };
          if (lotItem.batch !== '') cleanLotItem.batch = lotItem.batch;
          if (lotItem.expiry !== '') cleanLotItem.expiry = lotItem.expiry;
          if (lotItem?.stockItemIds)
            cleanLotItem.stock_item_ids = lotItem?.stockItemIds;
          if (lotItem.storageNodeId != null && lotItem.storageNodeId !== '') {
            const place = parseStoragePlace(
              storageTree,
              lotItem.storageNodeId,
              lotItem.slotIndex
            );
            cleanLotItem.storage_node_id = place.storage_node_id;
            cleanLotItem.slot_index = place.slot_index;
          }
          return cleanLotItem;
        });

        // Each item in the 'items' array is transformed to have the attributes: quote_item_id, quantity,
        // lot item details (batch, expiry and quantity), status, issue_detail.

        // Process kit component lots - match backend structure
        // Use Formik values to get the user-entered kit component lots data
        const cleanKitComponentLots = (formItem.kitComponentLots || []).map(
          kitComponentLot => {
            const cleanLots = (kitComponentLot.lots || []).map(lot => {
              let cleanLot = {
                lot_item_quantity: 1, // Backend expects quantity per lot item
              };
              if (lot.batch !== '') cleanLot.batch = lot.batch;
              if (lot.expiry !== '') cleanLot.expiry = lot.expiry;
              if (lot.storageLocation !== '')
                cleanLot.storage_location = lot.storageLocation;
              return cleanLot;
            });

            return {
              component_id: kitComponentLot.componentId,
              lots: cleanLots,
            };
          }
        );

        let finalItem = {
          quote_item_id: item.quoteItem?.id || item.quoteItem?.quote_item_id,
          cat_num: item.catNum,
          order_item_quantity: formItem.orderItemQuantity || 0,
          status: item.status,
          lot_items: cleanLotItems,
          kit_component_lots: cleanKitComponentLots,
        };

        // For "Did not arrive" items, ensure proper data structure
        if (item.status === 'Did not arrive') {
          finalItem.order_item_quantity = 0;
          finalItem.lot_items = [];
          finalItem.kit_component_lots = [];
          finalItem.issue_detail = item.issueDetail || '';
        } else if (item.status === 'Other') {
          finalItem.issue_detail = item.issueDetail;
        }

        return finalItem;
      });

      // If the order object exists, it means we are updating an existing order.
      if (orderObj) {
        // add for each lot item it's identifying quote_item_id and an empty quote_item
        finalItems = finalItems.map(item => ({
          ...item,
          quote_item_id: item.quote_item_id,
          quote_item: undefined,
        }));

      }

      // Collect all validated data that will be sent to the server in the form.
      const updatedOrderData = {
        quote: orderObj ? orderObj.quote.id : relatedQuoteObj.id,
        reception_dates: JSON.stringify(values.receptionDates),
        items: isPrepaymentOrder
          ? JSON.stringify([])
          : JSON.stringify(finalItems),
        corporate_order_ref: values.corporateOrderRef,
        exclude_from_finance_digest: Boolean(
          values.excludeFromFinanceDigest
        ),
      };

      logInfo('OrderModal', 'exclude_from_finance_digest on submit', {
        orderId: orderObj?.id ?? null,
        excludeFromFinanceDigest: updatedOrderData.exclude_from_finance_digest,
      });

      if (!isPrepaymentOrder) {
        updatedOrderData.received_by = values.receivedBy;
      }

      // Optional: pay from supplier credit (send empty/null when "None" so backend can unlink on edit)
      // Supplier credit selection and amount are now handled during quote creation.

      // Include project: prop > dropdown selection > order's project > quote's project
      const projectToUse =
        projectId != null
          ? typeof projectId === 'object'
            ? projectId.id
            : projectId
          : selectedProject
            ? selectedProject
            : orderObj?.project != null
              ? typeof orderObj.project === 'object'
                ? orderObj.project.id
                : orderObj.project
              : relatedQuoteObj?.project != null
                ? typeof relatedQuoteObj.project === 'object'
                  ? relatedQuoteObj.project.id
                  : relatedQuoteObj.project
                : null;
      if (projectToUse) {
        updatedOrderData.project = projectToUse;
      }

      if (selectedUser) {
        updatedOrderData.user = selectedUser;
      }

      let submittedQuoteFundingFields = null;

      if (orderObj && !isPrepaymentOrder) {
        const fundingFormValues =
          orderFundingSourceType === 'credit'
            ? { budgetId: '', fundingCreditId: orderFundingCreditId }
            : {
                budgetId: orderFundingBudgetId || '',
                fundingCreditId: '',
              };
        logInfo('OrderModal', 'submit order funding fields', {
          orderId: orderObj.id,
          fundingSourceType: orderFundingSourceType,
          budgetId: fundingFormValues.budgetId,
          fundingCreditId: fundingFormValues.fundingCreditId,
        });
        const fundingResult = buildOrderQuoteFundingPatchFields(
          orderObj,
          relatedQuoteObj,
          fundingFormValues
        );
        if (!fundingResult.ok) {
          showToast(fundingResult.error, 'error', 'top-right', 5000);
          setIsSubmitting(false);
          return;
        }
        Object.assign(updatedOrderData, fundingResult.fields);
        submittedQuoteFundingFields = fundingResult.fields;
      }

      const poPending = pendingOrderFileRows(poFiles);
      const receiptPending = pendingOrderFileRows(receiptFiles);

      if (orderObj) {
        const toDelete = buildOrderFilesToDelete(
          orderObj,
          poFiles,
          receiptFiles
        );
        if (
          toDelete.po_file_ids.length > 0 ||
          toDelete.receipt_file_ids.length > 0
        ) {
          updatedOrderData.order_files_to_delete =
            JSON.stringify(toDelete);
        }
      }

      if (poPending.length > 0 || receiptPending.length > 0) {
        updatedOrderData.order_files = buildOrderFilesManifestJson({
          poPending,
          receiptPending,
        });
      }

      if (!orderObj && notifySupplier && poPending.length > 0) {
        updatedOrderData.notify_supplier = true;
      }

      const mergePatchedOrder = patchResponse =>
        applySubmittedQuoteFundingToOrder(
          mergeOrderAfterUpdate(orderObj, patchResponse),
          submittedQuoteFundingFields
        );

      const orderPromise = orderObj
        ? updateOrder(token, orderObj.id, updatedOrderData, {
            poPending,
            receiptPending,
          })
        : createOrder(token, updatedOrderData, {
            poPending,
            receiptPending,
          });

      //Once the promise resolves (i.e., the request is completed), we look at the response. If it was successful,
      // we perform necessary UI update and call the onSuccessfulSubmit function and make sure to update
      // the orderUpdated context.
      orderPromise
        .then(response => {
          // Check if the order was actually updated successfully (we have updatedOrder)
          // The success flag might be false due to image upload issues, but the order update succeeded
          if (response && (response.success || response.updatedOrder)) {
            setTimeout(() => {
              if (orderObj) {
                const mergedOrder = mergePatchedOrder(response.updatedOrder);
                OrderCache.set(orderObj.id, mergedOrder);
                applyMergedOrderToList(mergedOrder);
                if (onOrderUpdated && mergedOrder) {
                  onOrderUpdated(orderObj.id, mergedOrder);
                }
              } else {
                if (onSuccessfulSubmit) onSuccessfulSubmit();
                if (clearSearchValue) clearSearchValue();
                filterOpenQuoteById(relatedQuoteObj.id);
              }

              // Refresh project data if order is linked to a project and callback is provided
              if ((projectId != null || selectedProject) && onProjectUpdate) {
                onProjectUpdate();
              }

              response.toast();
              handleClose();
              setIsSubmitting(false);
              refreshKitsAfterReception(response.updatedOrder);
              if (orderObj) setOrderUpdated(true);
            }, 2000);
          } else {
            // Check if response is an array (error messages from server)
            if (Array.isArray(response)) {
              showToast(
                response.join(', ') ||
                  'An error occurred while updating the order.',
                'error',
                'top-right',
                3000
              );
            } else {
              // Check if order update succeeded but image upload failed
              if (response.updatedOrder) {
                showToast(
                  'Order updated successfully, but there was an issue with image upload.',
                  'warning',
                  'top-right',
                  3000
                );
                // Still close the modal and update the order since the order itself was successful
                setTimeout(() => {
                  if (orderObj) {
                    const mergedOrder = mergePatchedOrder(response.updatedOrder);
                    OrderCache.set(orderObj.id, mergedOrder);
                    applyMergedOrderToList(mergedOrder);
                    if (onOrderUpdated && mergedOrder) {
                      onOrderUpdated(orderObj.id, mergedOrder);
                    }
                  }
                  handleClose();
                  setIsSubmitting(false);
                  refreshKitsAfterReception(response.updatedOrder);
                  if (orderObj) setOrderUpdated(true);
                }, 2000);
              } else {
                // If there was an error, we halt the submission process and display a notification with the error message.
                showToast(
                  'An unexpected error occurred. Please try again in a little while.',
                  'error',
                  'top-right',
                  3000
                );
              }
            }
            setIsSubmitting(false);
          }
        })
        .catch(error => {
          showToast(
            'An unexpected error occurred. Please try again in a little while.',
            'error',
            'top-right',
            3000
          );
          setIsSubmitting(false);
        });
    } catch (error) {
      showToast(
        'An unexpected error occurred. Please try again in a little while.',
        'error',
        'top-right',
        3000
      );
      setIsSubmitting(false);
    }
  };

  if (isViewer) {
    return null;
  }

  return (
    <>
      {/* Render Create Order or Edit buttons based on whether orderObj is present,
       onClick calls a function to handle modal visibility */}
      {!homeShowModal && (
        <Button
          variant={orderObj ? 'outline-success' : 'success'}
          onClick={handleShow}
          onMouseEnter={() => {
            // Only fetch quotes select list when creating new order (not editing)
            if (!orderObj) {
              fetchOpenQuotesSelectList();
            }
            // Fetch order details for editing mode
            if (onEditButtonHover) {
              onEditButtonHover();
            }
          }}
        >
          {orderObj ? <PencilFill /> : 'Create Order'}
        </Button>
      )}

      <Modal
        show={showModal}
        onHide={handleClose}
        backdrop="static"
        size="xl"
        dialogClassName="procure-modal-wide procure-modal-glass"
      >
        <Modal.Header closeButton className="procure-modal__header">
          <Modal.Title>
            {orderObj ? `Edit Order #${orderObj.id}` : 'Create Order'}
          </Modal.Title>
        </Modal.Header>

        {/* Show loading spinner when editing and data is being fetched */}
        {orderObj && (isEditButtonLoading || isLoadingLinkedQuote) ? (
          <Modal.Body
            className="procure-modal__body d-flex justify-content-center align-items-center"
            style={{ minHeight: '200px' }}
          >
            <div className="text-center">
              <Spinner animation="border" size="lg" className="mb-3" />
              <div className="text-muted">
                {isLoadingLinkedQuote
                  ? 'Loading linked quote...'
                  : 'Loading order details...'}
              </div>
            </div>
          </Modal.Body>
        ) : (
          /* The Formik component is being utilized for form handling,
           providing APIs for commonly needed form operations. */
          /* It uses 'orderObj' to conditionally setup its properties
          for the modal's create or edit forms. */
          <Formik
            // Keep key stable for in-form edits; explicit counter handles intentional hard resets.
            key={`order-${orderObj?.id || 'create'}-${formKeyCounter}`}
            enableReinitialize={true}
            initialTouched={
              orderObj && orderObj.items
                ? {
                    items:
                      orderObj.items && orderObj.items.length > 0
                        ? orderObj.items.map(item => ({
                            lotItems:
                              item.lot_items && item.lot_items.length > 0
                                ? item.lot_items.map(() => ({
                                    expiry: true,
                                    batch: true,
                                    lotItemQuantity: true,
                                  }))
                                : [],
                            storageLocation: true,
                            itemFulfilled: true,
                            otherReasonDetail: true,
                          }))
                        : [],
                    quoteList: true,
                    receptionDates: true,
                    receivedBy: true,
                    corporateOrderRef: true,
                  }
                : {
                    items:
                      items && items.length > 0
                        ? items.map(item => ({
                            lotItems:
                              item.lotItems && item.lotItems.length > 0
                                ? item.lotItems.map(() => ({
                                    expiry: false,
                                    batch: false,
                                    lotItemQuantity: false,
                                  }))
                                : [],
                            storageLocation: false,
                            itemFulfilled: false,
                            otherReasonDetail: false,
                          }))
                        : [],
                    quoteList: false,
                    receptionDates:
                      allReceivedOK || allDidNotArrive ? true : false,
                    receivedBy: allReceivedOK || allDidNotArrive ? true : false,
                    corporateOrderRef:
                      allReceivedOK || allDidNotArrive ? true : false,
                  }
            }
            initialValues={{
              items: itemsToFormItems(items),
              quoteList: relatedQuoteObj ? relatedQuoteObj.id : '',
              receptionDates: receptionDatesValue,
              receivedBy: receivedByValue,
              corporateOrderRef: corporateOrderRefValue,
              excludeFromFinanceDigest: excludeFromFinanceDigestValue,
            }}
            validateOnMount={!!orderObj}
            validationSchema={formSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({
              handleChange,
              handleSubmit,
              values,
              handleBlur,
              touched,
              errors,
              setFieldTouched,
              isValid,
              dirty,
              setFieldValue,
              setValues,
            }) => {
              // Formik provides these props to be utilized in the form fields for handling form state.
              return (
                // The form itself
                <Form noValidate onSubmit={handleSubmit}>
                  {/* Some more components with their respective forms and form fields */}
                  {/* Additionally, there's conditional rendering to dynamically create JSX based on whether
                 you're editing or creating an order. */}
                  <Modal.Body className="procure-modal__body d-flex flex-column">
                    {!orderObj && (
                      <div className="order-modal-quote-row order-glass-panel">
                        <Form.Group
                          as={Col}
                          md="8"
                          controlId="selectQuote"
                          className="field-margin mb-0"
                        >
                          <Form.Label>Select Quote</Form.Label>
                          {isLoadingQuotes &&
                          openQuotesSelectList.length === 0 ? (
                            <div className="d-flex align-items-center">
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              <span className="text-muted">
                                Loading quotes...
                              </span>
                            </div>
                          ) : (
                            <Form.Select
                              name="quoteList"
                              disabled={!openQuotesSelectList.length}
                              value={values.quoteList}
                              onChange={event => {
                                const { value } = event.target;
                                handleChange(event);
                                fetchQuote(value);
                              }}
                            >
                              <option value="" disabled>
                                -- Select Quote --
                              </option>
                              {openQuotesSelectList.map((choice, index) => (
                                <option key={index} value={choice.value}>
                                  {choice.label}
                                </option>
                              ))}
                            </Form.Select>
                          )}
                          <Form.Control.Feedback type="invalid">
                            {errors.quoteList}
                          </Form.Control.Feedback>
                          {!relatedQuoteObj && (
                            <Form.Text>
                              {!openQuotesSelectList.length
                                ? 'No open quotes available'
                                : `Choose a quote to view it's details`}
                            </Form.Text>
                          )}
                        </Form.Group>

                        {renderQuotePdfLink()}
                      </div>
                    )}

                    {/* Order-level payment credit (fulfillment), not quote funding */}
                    {!isPrepaymentOrder && orderObj?.supplier_credit != null && (
                        <div className="order-glass-panel order-glass-panel--compact">
                          <Form.Group as={Col} md="8" className="field-margin">
                            <Form.Label>Order paid from credit</Form.Label>
                            <div className="d-flex flex-column">
                              <span className="small">
                                {(() => {
                                  const credit = orderObj.supplier_credit;
                                  if (!credit) return null;
                                  if (typeof credit === 'object') {
                                    const baseLabel =
                                      credit.name ||
                                      (credit.order
                                        ? typeof credit.order === 'object'
                                          ? credit.order.corporate_order_ref ||
                                            `Order #${credit.order.id}`
                                          : `Order #${credit.order}`
                                        : null) ||
                                      `Credit #${credit.id}`;
                                    const currencySuffix =
                                      credit.currency != null
                                        ? ` (${credit.currency})`
                                        : '';
                                    return `${baseLabel}${currencySuffix}`;
                                  }
                                  return `Credit #${credit}`;
                                })()}
                                {resolveOrderAmountCharged(orderObj) != null && (
                                    <>
                                      {' '}
                                      – charged: {resolveOrderAmountCharged(orderObj)}
                                      {orderObj?.supplier_credit?.currency
                                        ? ` ${orderObj.supplier_credit.currency}`
                                        : ''}
                                    </>
                                  )}
                              </span>
                              <span className="text-muted small mt-1">
                                Order-level payment record. Quote funding is
                                edited above.
                              </span>
                            </div>
                          </Form.Group>
                        </div>
                      )}

                    {/* PDF Upload Component - Now positioned below the quote select list */}
                    {!orderObj && !relatedQuoteObj && items.length === 0 && (
                      <div className="procure-file-flow-panel mt-3">
                        <PdfUploadComponent
                          defaultReceivedBy={pdfOcrDefaultReceivedBy}
                          onPdfProcessed={result => {
                            const shouldClose =
                              result?.reviewedSubmit === true ||
                              result?.directUploadComplete === true;
                            if (shouldClose) {
                              // Call the onSuccessfulSubmit function to refetch orders
                              if (onSuccessfulSubmit) {
                                onSuccessfulSubmit();
                              }
                              // Close the modal after successful PDF upload
                              handleClose();
                            }
                          }}
                          disabled={!openQuotesSelectList.length}
                          token={token}
                        />
                      </div>
                    )}

                    {/* Displaying Items */}
                    {items && (relatedQuoteObj || orderObj) && (
                      <>
                        <div
                          className={
                            orderObj ? 'order-modal-supplier-row' : undefined
                          }
                        >
                        <div className="order-modal-supplier-bar order-glass-panel order-modal-supplier-bar--compact">
                        <div className="order-modal-supplier-bar__inner">
                        {(orderObj?.supplier ?? relatedQuoteObj?.supplier) && (
                          <SupplierDetailModal
                            supplierObj={
                              orderObj?.supplier ?? relatedQuoteObj?.supplier
                            }
                            supplierId={
                              (orderObj?.supplier ?? relatedQuoteObj?.supplier)
                                ?.id
                            }
                            prefetchOnHover={false}
                          />
                        )}

                        {!isPrepaymentOrder && (
                          <Form.Group className="order-modal-bulk-actions mb-0">
                            <Button
                              variant={
                                allDidNotArrive ? 'danger' : 'outline-danger'
                              }
                              onClick={() =>
                                handleAllDidNotArriveToggle(!allDidNotArrive)
                              }
                              title="Mark all as Not Arrived"
                            >
                              {allDidNotArrive
                                ? '❌ Revert to initial status'
                                : '📦 Mark all as Not Arrived'}
                            </Button>

                            <Button
                              variant={
                                allReceivedOK ? 'success' : 'outline-success'
                              }
                              onClick={() =>
                                handleAllReceivedOKToggle(
                                  !allReceivedOK,
                                  newItems => {
                                    const formItems =
                                      itemsToFormItems(newItems);
                                    if (
                                      process.env.NODE_ENV === 'development'
                                    ) {
                                      logInfo(
                                        'OrderModal',
                                        'Mark all OK: calling setValues with new form items',
                                        {
                                          currentFormValuesItemsLength: (
                                            values.items || []
                                          ).length,
                                          newFormItemsSummary: formItems.map(
                                            (it, i) => ({
                                              index: i,
                                              orderItemQuantity:
                                                it?.orderItemQuantity,
                                              itemFulfilled: it?.itemFulfilled,
                                              lotItemsLength: (
                                                it?.lotItems || []
                                              ).length,
                                            })
                                          ),
                                        }
                                      );
                                    }
                                    // Defer form update to next tick so it runs after React commit and
                                    // any enableReinitialize/effects; avoids our update being overwritten.
                                    const valuesSnapshot = {
                                      ...values,
                                      items: formItems,
                                    };
                                    setTimeout(() => {
                                      setValues(valuesSnapshot);
                                      formItems.forEach((formItem, index) => {
                                        setFieldValue(
                                          `items[${index}].orderItemQuantity`,
                                          formItem.orderItemQuantity
                                        );
                                        setFieldValue(
                                          `items[${index}].itemFulfilled`,
                                          formItem.itemFulfilled
                                        );
                                        setFieldValue(
                                          `items[${index}].selectedReason`,
                                          formItem.selectedReason
                                        );
                                        setFieldValue(
                                          `items[${index}].otherReasonDetail`,
                                          formItem.otherReasonDetail || ''
                                        );
                                        setFieldValue(
                                          `items[${index}].storageLocation`,
                                          formItem.storageLocation || ''
                                        );
                                        (formItem.lotItems || []).forEach(
                                          (lot, lotIndex) => {
                                            setFieldValue(
                                              `items[${index}].lotItems[${lotIndex}].lotItemQuantity`,
                                              lot.lotItemQuantity
                                            );
                                            setFieldValue(
                                              `items[${index}].lotItems[${lotIndex}].batch`,
                                              lot.batch ?? ''
                                            );
                                            setFieldValue(
                                              `items[${index}].lotItems[${lotIndex}].expiry`,
                                              lot.expiry ?? ''
                                            );
                                            setFieldValue(
                                              `items[${index}].lotItems[${lotIndex}].storageNodeId`,
                                              lot.storageNodeId ?? null
                                            );
                                            setFieldValue(
                                              `items[${index}].lotItems[${lotIndex}].slotIndex`,
                                              lot.slotIndex ?? null
                                            );
                                            if (lot.storageNodeId != null && lot.storageNodeId !== '') {
                                              logInfo(
                                                'OrderModal',
                                                'Mark all OK kept reception lot place',
                                                {
                                                  orderItemIndex: index,
                                                  lotIndex,
                                                  storageNodeId: lot.storageNodeId,
                                                  slotIndex: lot.slotIndex ?? null,
                                                }
                                              );
                                            }
                                          }
                                        );
                                      });
                                    }, 0);
                                  }
                                )
                              }
                              title="Mark all as Received OK"
                            >
                              {allReceivedOK
                                ? '✅ Revert to initial Status'
                                : '📥 Mark all as Received OK'}
                            </Button>
                          </Form.Group>
                        )}
                        </div>
                        </div>
                        {orderObj && renderQuotePdfLink()}
                        </div>

                        {/* Mapping through items */}
                        {isPrepaymentOrder ? (
                          <p className="text-muted mt-3">
                            Prepayment order — no items
                          </p>
                        ) : (
                          items.map((item, index) =>
                            (
                              orderObj
                                ? orderObj.items[index]
                                : relatedQuoteObj.items[index]
                            ) ? (
                              <OrderItemComponent
                                key={
                                  orderObj
                                    ? `${orderObj.quote.id}-${index}`
                                    : `${relatedQuoteObj.id}-${index}`
                                }
                                product={
                                  orderObj
                                    ? orderObj.items[index].product
                                    : relatedQuoteObj.items[index].product
                                }
                                orderObjItemLotItems={
                                  orderObj
                                    ? orderObj.items[index].lotItems
                                    : null
                                }
                                orderObjItemQuantity={
                                  orderObj
                                    ? orderObj.items[index].quantity
                                    : null
                                }
                                onItemChange={updateItem}
                                orderItemIndex={index}
                                item={item}
                                allReceivedOK={allReceivedOK}
                                quoteItem={
                                  orderObj ? null : relatedQuoteObj.items[index]
                                }
                                orderItemStatus={
                                  orderObj ? orderObj.items[index].status : null
                                }
                                addLotItem={addLotItem}
                                handleLotItemDelete={deleteLotItem}
                                storageNodes={storageTree}
                                storagePlaceOptions={storagePlaceOptions}
                                formik={{
                                  handleChange,
                                  values,
                                  handleBlur,
                                  touched,
                                  errors,
                                  setFieldTouched,
                                  setFieldValue,
                                  setValues,
                                }}
                              />
                            ) : null
                          )
                        )}
                      </>
                    )}

                    {/* Other Order Attributes */}
                    {(relatedQuoteObj || orderObj) && (
                      <div className="order-modal-details-panel order-glass-panel">
                        <OrderFilesUploadPanels
                          poFiles={poFiles}
                          receiptFiles={receiptFiles}
                          onPoFilesChange={files =>
                            appendPendingFiles(setPoFiles, files)
                          }
                          onReceiptFilesChange={files =>
                            appendPendingFiles(setReceiptFiles, files)
                          }
                          onDeletePo={handleDeletePoFile}
                          onDeleteReceipt={handleDeleteReceiptFile}
                          documentsError={orderDocumentsError}
                          showDocumentsRequirement={!isPrepaymentOrder}
                          catalogueNumber={values.corporateOrderRef}
                          showNotifySupplierCheckbox={!orderObj}
                          hasPendingPo={pendingOrderFileRows(poFiles).length > 0}
                          notifySupplier={notifySupplier}
                          onNotifySupplierChange={setNotifySupplier}
                        />

                        {/* Field for Reception Dates */}
                        <Form.Group
                          controlId="formReceptionDates"
                          className="field-margin"
                        >
                          <Form.Label>
                            {isPrepaymentOrder
                              ? 'Sent to Supplier on:'
                              : 'Reception Dates'}{' '}
                            <RequiredAsteriskComponent />
                          </Form.Label>
                          {values.receptionDates &&
                            values.receptionDates.map((date, index) => (
                              <div
                                key={index}
                                className="d-flex align-items-center mb-2"
                              >
                                <DatePicker
                                  selected={date ? new Date(date) : null}
                                  onChange={selectedDate => {
                                    if (selectedDate) {
                                      const year = selectedDate.getFullYear();
                                      const month = String(
                                        selectedDate.getMonth() + 1
                                      ).padStart(2, '0');
                                      const day = String(
                                        selectedDate.getDate()
                                      ).padStart(2, '0');
                                      const dateValue = `${year}-${month}-${day}`;

                                      if (year.toString().length <= 4) {
                                        const newDates = [
                                          ...values.receptionDates,
                                        ];
                                        newDates[index] = dateValue;
                                        setFieldValue(
                                          'receptionDates',
                                          newDates
                                        );
                                        setReceptionDatesValue(newDates);
                                      }
                                    } else {
                                      const newDates = [
                                        ...values.receptionDates,
                                      ];
                                      newDates[index] = '';
                                      setFieldValue('receptionDates', newDates);
                                      setReceptionDatesValue(newDates);
                                    }
                                  }}
                                  dateFormat="dd/MM/yyyy"
                                  placeholderText="dd/mm/yyyy"
                                  className={`form-control me-2 ${
                                    errors.receptionDates?.[index]
                                      ? 'is-invalid'
                                      : !errors.receptionDates?.[index] && date
                                        ? 'is-valid'
                                        : ''
                                  }`}
                                  wrapperClassName="me-2"
                                  inputClassName="text-center"
                                  showYearDropdown
                                  showMonthDropdown
                                  dropdownMode="select"
                                  todayButton="Today"
                                />
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => {
                                    const newDates =
                                      values.receptionDates.filter(
                                        (_, i) => i !== index
                                      );
                                    setFieldValue('receptionDates', newDates);
                                    setReceptionDatesValue(newDates);
                                  }}
                                  disabled={values.receptionDates.length === 1}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              const newDates = [
                                ...values.receptionDates,
                                new Date().toISOString().split('T')[0],
                              ];
                              setFieldValue('receptionDates', newDates);
                              setReceptionDatesValue(newDates);
                            }}
                            className="mt-2"
                          >
                            Add Reception Date
                          </Button>
                          {errors.receptionDates &&
                            typeof errors.receptionDates === 'string' && (
                              <Form.Control.Feedback
                                type="invalid"
                                className="d-block"
                              >
                                {errors.receptionDates}
                              </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        {/* Field for Receiver */}
                        {!isPrepaymentOrder && (
                          <Form.Group
                            controlId="receivedBy"
                            className="field-margin"
                          >
                            <Form.Label>
                              Received By <RequiredAsteriskComponent />
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="receivedBy"
                              value={values.receivedBy}
                              onChange={e => {
                                handleChange(e);
                                setReceivedByValue(e.target.value);
                              }}
                              onBlur={handleBlur}
                              isValid={!errors.receivedBy && values?.receivedBy}
                              isInvalid={
                                (touched?.receivedBy && !values?.receivedBy) ||
                                (errors?.receivedBy &&
                                  errors?.receivedBy !==
                                    'Received by is required' &&
                                  values?.receivedBy)
                              }
                            />
                            <Form.Control.Feedback type="valid">
                              Looks good!
                            </Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                              {errors.receivedBy}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}
                        <Form.Group
                          controlId="corporateOrderRef"
                          className="field-margin"
                        >
                          <Form.Label>
                            Order Reference <RequiredAsteriskComponent />
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="corporateOrderRef"
                            value={values.corporateOrderRef}
                            onChange={e => {
                              handleChange(e);
                              setCorporateOrderRefValue(e.target.value);
                            }}
                            onBlur={handleBlur}
                            isValid={
                              !errors.corporateOrderRef &&
                              values?.corporateOrderRef
                            }
                            isInvalid={
                              (touched?.corporateOrderRef &&
                                !values?.corporateOrderRef) ||
                              (errors?.corporateOrderRef &&
                                errors?.corporateOrderRef !==
                                  'Order reference field is required.' &&
                                values?.corporateOrderRef)
                            }
                          />
                          <Form.Control.Feedback type="valid">
                            Looks good!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.corporateOrderRef}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group
                          controlId="formExcludeFromFinanceDigest"
                          className="field-margin"
                        >
                          <Form.Check
                            type="checkbox"
                            name="excludeFromFinanceDigest"
                            label="Exclude from finance digest"
                            checked={Boolean(values.excludeFromFinanceDigest)}
                            onChange={e => {
                              const checked = e.target.checked;
                              setFieldValue('excludeFromFinanceDigest', checked);
                              setExcludeFromFinanceDigestValue(checked);
                            }}
                          />
                        </Form.Group>

                        {/* Optional Project, User, and quote Funding (edit order) */}
                        <Row className="field-margin">
                          <Col
                            md={
                              orderObj &&
                              isOrderQuoteFundingEditable(
                                orderObj,
                                relatedQuoteObj,
                                { isPrepayment: isPrepaymentOrder }
                              )
                                ? 4
                                : 6
                            }
                          >
                            <Form.Group controlId="orderProject">
                              <Form.Label>Project (optional)</Form.Label>
                              {isLoadingProjects ? (
                                <div className="d-flex align-items-center">
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  <span className="text-muted">
                                    Loading projects...
                                  </span>
                                </div>
                              ) : (
                                <DropdownSelect
                                  optionsList={projectSelectList}
                                  label="Project"
                                  selectedValue={
                                    projectSelectList.find(
                                      p => p.value === selectedProject
                                    ) || null
                                  }
                                  setSelectedValue={option => {
                                    setSelectedProject(option?.value ?? null);
                                  }}
                                  placeholder="Select project (optional)"
                                  disabledOptions={[]}
                                />
                              )}
                              <Form.Text className="text-muted">
                                Link this order to a project
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col
                            md={
                              orderObj &&
                              isOrderQuoteFundingEditable(
                                orderObj,
                                relatedQuoteObj,
                                { isPrepayment: isPrepaymentOrder }
                              )
                                ? 4
                                : 6
                            }
                          >
                            <Form.Group controlId="orderUser">
                              <Form.Label>User (optional)</Form.Label>
                              {isLoadingUsers ? (
                                <div className="d-flex align-items-center">
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  <span className="text-muted">
                                    Loading users...
                                  </span>
                                </div>
                              ) : (
                                <DropdownSelect
                                  optionsList={userDropdownOptions}
                                  label="User"
                                  selectedValue={findUserOption(
                                    userDropdownOptions,
                                    selectedUser
                                  )}
                                  setSelectedValue={option => {
                                    const v = option?.value;
                                    setSelectedUser(
                                      option == null || v === '' ? null : v
                                    );
                                  }}
                                  placeholder="Select user (optional)"
                                  disabledOptions={[]}
                                />
                              )}
                              <Form.Text className="text-muted">
                                User to notify for this order.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          {orderObj &&
                            isOrderQuoteFundingEditable(
                              orderObj,
                              relatedQuoteObj,
                              { isPrepayment: isPrepaymentOrder }
                            ) && (
                              <Col md={4}>
                                <Form.Group controlId="orderFundingSource">
                                  <Form.Label>
                                    Funding{' '}
                                    <RequiredAsteriskComponent />
                                  </Form.Label>
                                  <Form.Select
                                    name="orderFundingSourceType"
                                    value={orderFundingSourceType || 'budget'}
                                    className="mb-2"
                                    onChange={e => {
                                      const nextType = e.target.value;
                                      setOrderFundingSourceType(nextType);
                                      if (nextType === 'budget') {
                                        setOrderFundingCreditId('');
                                      } else {
                                        setOrderFundingBudgetId('');
                                      }
                                    }}
                                  >
                                    <option value="budget">Budget</option>
                                    <option value="credit">
                                      Supplier credit
                                    </option>
                                  </Form.Select>
                                  {orderFundingSourceType === 'credit' ? (
                                    <>
                                      {isLoadingSupplierCredits ? (
                                        <div className="d-flex align-items-center">
                                          <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                          />
                                          <span className="text-muted">
                                            Loading credits...
                                          </span>
                                        </div>
                                      ) : (
                                        <Form.Select
                                          name="orderFundingCreditId"
                                          value={orderFundingCreditId || ''}
                                          onChange={e => {
                                            setOrderFundingCreditId(
                                              e.target.value || ''
                                            );
                                          }}
                                        >
                                          <option value="">
                                            Select supplier credit
                                          </option>
                                          {supplierCredits.map(credit => (
                                            <option
                                              key={credit.id}
                                              value={credit.id}
                                            >
                                              {credit.name ||
                                                `Credit #${credit.id}`}{' '}
                                              – remaining{' '}
                                              {credit.remaining_amount}{' '}
                                              {credit.currency}
                                            </option>
                                          ))}
                                        </Form.Select>
                                      )}
                                      <Form.Text className="text-muted">
                                        Amount is calculated from quote line
                                        items.
                                      </Form.Text>
                                    </>
                                  ) : (
                                    <>
                                      <BudgetLineSelect
                                        token={token}
                                        value={orderFundingBudgetId || ''}
                                        onChange={v => {
                                          setOrderFundingBudgetId(
                                            v != null ? String(v) : ''
                                          );
                                        }}
                                        label="Budget"
                                        required
                                      />
                                      <Form.Text className="text-muted">
                                        Saves to the linked quote. Mutually
                                        exclusive with supplier credit.
                                      </Form.Text>
                                    </>
                                  )}
                                </Form.Group>
                              </Col>
                            )}
                        </Row>

                        {/* Project Association Indicator */}
                        {(projectId != null ||
                          selectedProject != null ||
                          orderObj?.project != null ||
                          relatedQuoteObj?.project != null) && (
                          <Form.Group className="field-margin">
                            <Form.Label>Associated Project</Form.Label>
                            {isLoadingProject ? (
                              <div className="d-flex align-items-center">
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                <span className="text-muted">
                                  Loading project info...
                                </span>
                              </div>
                            ) : projectInfo ? (
                              <div className="order-modal-info-card">
                                <div className="d-flex align-items-center justify-content-between">
                                  <div>
                                    <strong>{projectInfo.name}</strong>
                                    {projectInfo.status && (
                                      <span className="ms-2">
                                        <Badge bg="secondary">
                                          {projectInfo.status}
                                        </Badge>
                                      </span>
                                    )}
                                  </div>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                      handleClose();
                                      navigate(`/projects/${projectInfo.id}`);
                                    }}
                                  >
                                    View Project
                                  </Button>
                                </div>
                                <Form.Text className="text-muted d-block mt-1">
                                  This order is linked to a project. Project
                                  statuses will update automatically.
                                </Form.Text>
                              </div>
                            ) : (
                              <div className="order-modal-info-card">
                                <Form.Text className="text-muted mb-0">
                                  Project association exists but details could
                                  not be loaded.
                                </Form.Text>
                              </div>
                            )}
                          </Form.Group>
                        )}
                      </div>
                    )}
                  </Modal.Body>
                  <Modal.Footer className="procure-modal__footer">
                    {/* Buttons for submitting and closing modal */}
                    {isSubmitting ? (
                      <Button variant="primary" className="glass-primary-btn" disabled>
                        <Spinner
                          size="sm"
                          as="span"
                          animation="border"
                          role="status"
                          aria-hidden="true"
                        />
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        className="glass-primary-btn"
                        disabled={
                          quantityError ||
                          !isValid ||
                          !hasRequiredOrderDocuments
                        }
                        onClick={handleSubmit}
                      >
                        {orderObj ? 'Save' : 'Create'}
                      </Button>
                    )}

                    <Button
                      variant="secondary"
                      className="procure-modal__close-btn"
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        )}
      </Modal>

    </>
  );
};
export default OrderModal;
